import { ASSETS } from '@jira/platform__application-editions/src/application-key.tsx';
import { useAssetsUserPermission } from '@jira/router-resources__assets-user-permission/src/useAssetsUserPermission.tsx';
import { useTenantContextHook } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AssetsTenantContext } from './AssetsTenantContext.types.tsx';

/**
 * Hook that provides Assets-specific tenant context with role-based permissions.
 * When the feature gate is enabled, overrides admin permissions with Assets-specific roles.
 */
export const useAssetsTenantContext = (): AssetsTenantContext => {
	const [{ tenantContext }] = useTenantContextHook();
	const { isAssetsAdmin, isLicensedAssetsUser } = useAssetsUserPermission();

	const hasAssetsEntitlement =
		tenantContext.licensedProducts && tenantContext.licensedProducts[ASSETS];

	// Override admin permissions with Assets-specific role when feature gate is enabled.
	// Assets-entitled sites use the kill-switch roles; Units secondary sites have no
	// local Assets entitlement but still resolve roles via the Assets permission API.
	if (
		(hasAssetsEntitlement && fg('aurum-997-license-kill-switch')) ||
		fg('assets_access_workspace_source_of_truth')
	) {
		return {
			...tenantContext,
			isAdmin: !!isAssetsAdmin,
			isSiteAdmin: !!isAssetsAdmin,
			isLicensedAssetsUser: !!isLicensedAssetsUser,
		};
	}

	return tenantContext;
};

/**
 * Hook that returns whether the current user is an Assets administrator.
 */
export const useAssetsIsAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isAdmin ?? false;
};

/**
 * Hook that returns whether the current user is an Assets site administrator.
 */
export const useAssetsIsSiteAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isSiteAdmin ?? false;
};
